import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Page,
  Layout,
  Card,
  Text,
  TextField,
  Select,
  Button,
  Checkbox,
  Badge,
  Banner,
  Toast,
  Spinner,
  InlineStack,
  BlockStack,
  DataTable,
} from "@shopify/polaris";
import { usePriceLabels, type PriceLabelFilters } from "../hooks/usePriceLabels";

function formatCurrency(n: number): string {
  return `$${n.toFixed(2)}`;
}

export default function PriceLabels() {
  const { items, total, loading, error, filterOptions, loadLabels, printLabels, deleteLabel } =
    usePriceLabels();

  const [search, setSearch] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [printedFilter, setPrintedFilter] = useState("false"); // default: unprinted
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [printing, setPrinting] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const fetchLabels = useCallback(() => {
    const filters: PriceLabelFilters = { pageSize: 200 };
    if (search) filters.search = search;
    if (vendorFilter) filters.vendor = vendorFilter;
    if (typeFilter) filters.type = typeFilter;
    if (printedFilter) filters.printed = printedFilter;
    loadLabels(filters);
  }, [search, vendorFilter, typeFilter, printedFilter, loadLabels]);

  useEffect(() => {
    fetchLabels();
  }, [vendorFilter, typeFilter, printedFilter]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(fetchLabels, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)));
    }
  }, [items, selectedIds]);

  const handlePrint = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setPrinting(true);

    const blob = await printLabels(Array.from(selectedIds));
    if (blob) {
      // Open PDF in new tab
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");

      setToastMessage(`${selectedIds.size} label(s) generated and marked as printed`);
      setSelectedIds(new Set());
      fetchLabels(); // Refresh to update printed status
    }
    setPrinting(false);
  }, [selectedIds, printLabels, fetchLabels]);

  const handleDelete = useCallback(
    async (id: string) => {
      const success = await deleteLabel(id);
      if (success) {
        fetchLabels();
        setToastMessage("Label deleted");
      }
    },
    [deleteLabel, fetchLabels]
  );

  const allSelected = items.length > 0 && selectedIds.size === items.length;

  const rows = items.map((item) => [
    <Checkbox
      key={`sel-${item.id}`}
      label=""
      labelHidden
      checked={selectedIds.has(item.id)}
      onChange={() => handleToggleSelect(item.id)}
    />,
    item.sku || "—",
    (item.productTitle || "") +
      (item.variantTitle && item.variantTitle !== item.productTitle
        ? ` — ${item.variantTitle}`
        : ""),
    item.productVendor || "—",
    formatCurrency(item.oldPrice),
    <Text key={`np-${item.id}`} as="span" fontWeight="bold" tone={item.newPrice !== item.oldPrice ? "critical" : undefined}>
      {formatCurrency(item.newPrice)}
    </Text>,
    item.margin != null ? `${item.margin}%` : "—",
    <Badge key={`pr-${item.id}`} tone={item.labelPrinted ? "success" : "attention"}>
      {item.labelPrinted ? "Printed" : "Pending"}
    </Badge>,
    new Date(item.createdAt).toLocaleDateString("en-CA"),
  ]);

  return (
    <Page
      title="Price Labels"
      primaryAction={{
        content: `Print ${selectedIds.size} Label${selectedIds.size !== 1 ? "s" : ""}`,
        onAction: handlePrint,
        loading: printing,
        disabled: selectedIds.size === 0,
      }}
    >
      {error && (
        <Layout.Section>
          <Banner tone="critical"><p>{error}</p></Banner>
        </Layout.Section>
      )}

      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <TextField
                label=""
                labelHidden
                placeholder="Search by SKU or product name..."
                value={search}
                onChange={setSearch}
                clearButton
                onClearButtonClick={() => setSearch("")}
                autoComplete="off"
              />
              <InlineStack gap="200" wrap>
                <div style={{ minWidth: "140px" }}>
                  <Select
                    label="Status"
                    labelHidden
                    options={[
                      { label: "Unprinted", value: "false" },
                      { label: "Printed", value: "true" },
                      { label: "All", value: "" },
                    ]}
                    value={printedFilter}
                    onChange={setPrintedFilter}
                  />
                </div>
                {filterOptions.vendors.length > 0 && (
                  <div style={{ minWidth: "140px" }}>
                    <Select
                      label="Vendor"
                      labelHidden
                      options={[
                        { label: "All Vendors", value: "" },
                        ...filterOptions.vendors.map((v) => ({ label: v, value: v })),
                      ]}
                      value={vendorFilter}
                      onChange={setVendorFilter}
                    />
                  </div>
                )}
                {filterOptions.types.length > 0 && (
                  <div style={{ minWidth: "140px" }}>
                    <Select
                      label="Type"
                      labelHidden
                      options={[
                        { label: "All Types", value: "" },
                        ...filterOptions.types.map((t) => ({ label: t, value: t })),
                      ]}
                      value={typeFilter}
                      onChange={setTypeFilter}
                    />
                  </div>
                )}
                {(vendorFilter || typeFilter || printedFilter !== "false") && (
                  <Button
                    variant="plain"
                    onClick={() => {
                      setVendorFilter("");
                      setTypeFilter("");
                      setPrintedFilter("false");
                    }}
                  >
                    Clear filters
                  </Button>
                )}
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card padding="0">
            {loading ? (
              <div style={{ padding: "40px", textAlign: "center" }}>
                <Spinner size="large" />
              </div>
            ) : items.length === 0 ? (
              <div style={{ padding: "40px", textAlign: "center" }}>
                <Text as="p" tone="subdued">No price changes found</Text>
              </div>
            ) : (
              <DataTable
                columnContentTypes={["text", "text", "text", "text", "numeric", "numeric", "numeric", "text", "text"]}
                headings={[
                  <Checkbox
                    key="select-all"
                    label=""
                    labelHidden
                    checked={allSelected}
                    onChange={handleSelectAll}
                  />,
                  "SKU",
                  "Product",
                  "Vendor",
                  "Old Price",
                  "New Price",
                  "Margin",
                  "Status",
                  "Date",
                ]}
                rows={rows}
                hoverable
                increasedTableDensity
                footerContent={`${total} total price change${total !== 1 ? "s" : ""}`}
              />
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {toastMessage && (
        <Toast content={toastMessage} onDismiss={() => setToastMessage(null)} />
      )}
    </Page>
  );
}
